// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/components/HRComponents/RecruitmentDashboard.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Users, Briefcase, UserCheck, UserMinus, Clock, 
  TrendingUp, Calendar, Filter, X, Video, MapPin, Check 
} from 'lucide-react';
import { toast } from 'react-toastify';

import api from '../../common/apiClient';
import SummaryApi from '../../common/SummaryApi';
import PageLoader from '../../helpers/PageLoader';
import SelectDropdown from '../../customStyles/SelectDropdown';

// 👈 1. Imported the centralized stages directly to resolve friendly labels dynamically
import { DEFAULT_RECRUITMENT_STAGES } from '../../helpers/OrgCommonData';

const RecruitmentDashboard = ({ user }) => {
  const { portalName } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  // Filters State
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [divisionFilter, setDivisionFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');

  // Dropdown Option Lists
  const [departments, setDepartments] = useState([]);
  const [divisions, setDivisions] = useState([]);

  // Fetch filtering dropdown options (concurrently)
  const fetchFilterOptions = useCallback(async () => {
    try {
      const [deptRes, divRes] = await Promise.all([
        api.get(SummaryApi.getAllDepartments.url),
        api.get(SummaryApi.getAllDivisions.url)
      ]);
      if (deptRes.data.success) setDepartments(deptRes.data.data || []);
      if (divRes.data.success) setDivisions(divRes.data.data || []);
    } catch (err) {
      console.error('Error fetching dashboard filter options:', err);
    }
  }, []);

  // Fetch recruitment analytical metrics
  const fetchDashboardStats = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${SummaryApi.getRecruitmentDashboardStats?.url || '/api/hr/recruitment/dashboard/stats'}?`;
      if (startDate) url += `startDate=${startDate}&`;
      if (endDate) url += `endDate=${endDate}&`;
      if (divisionFilter) url += `divisionId=${divisionFilter}&`;
      if (deptFilter) url += `departmentId=${deptFilter}&`;

      const response = await api.get(url);
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      toast.error('Failed to load recruitment metrics.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, divisionFilter, deptFilter]);

  useEffect(() => {
    fetchFilterOptions();
    fetchDashboardStats();
  }, [fetchFilterOptions, fetchDashboardStats]);

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setDivisionFilter('');
    setDeptFilter('');
    toast.info('Dashboard filters cleared');
  };

  // Memoized computations for SVG Donut chart angles and stroke-dash arrays
  const donutData = useMemo(() => {
    if (!stats || !stats.sourcingChannels) return [];
    const channels = stats.sourcingChannels;
    const total = channels.reduce((sum, channel) => sum + channel.count, 0);
    
    let cumulativePercent = 0;
    return channels.map((channel) => {
      const percent = total > 0 ? (channel.count / total) * 100 : 0;
      const strokeDasharray = `${percent} ${100 - percent}`;
      const strokeDashoffset = 100 - cumulativePercent + 25; // start at top (12 o'clock)
      cumulativePercent += percent;
      return {
        ...channel,
        percent: percent.toFixed(1),
        strokeDasharray,
        strokeDashoffset
      };
    });
  }, [stats]);

  // 👈 2. Accumulate stages accurately into macro columns matching 'stageConfig.js' schemas
  const stageCounts = useMemo(() => {
    if (!stats || !stats.pipelineStages) return { applied: 0, screening: 0, interview: 0, offer: 0 };
    const stages = stats.pipelineStages;

    // Helper to filter and sum count arrays of multiple matching stages
    const sumCount = (stageKeys) => {
      return stages
        .filter(s => stageKeys.includes(s._id))
        .reduce((sum, s) => sum + s.count, 0);
    };

    return {
      applied: sumCount(['applied']),
      screening: sumCount(['resume_screening', 'shortlisted']),
      interview: sumCount(['interview_scheduled', 'interview_completed', 'selected']),
      offer: sumCount(['offered', 'onboarding']),
    };
  }, [stats]);

  // Helper to map technical stage keys to friendly labels defined in stageConfig.js
  const getStageLabel = (stageKey) => {
    return DEFAULT_RECRUITMENT_STAGES.find(s => s.key === stageKey)?.label || stageKey;
  };

  if (loading || !stats) return <PageLoader />;

  const kpis = stats.kpiCards || {};

  return (
    <div className="space-y-6">
      
      {/* Controls Card */}
      <div className="bg-white border rounded-xl p-4 shadow-sm space-y-4">
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div>
            <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Dashboard Controls</h2>
            <p className="text-[10px] text-gray-400 mt-0.5">Filter statistics by dates, departments, or divisions.</p>
          </div>
          <button 
            onClick={() => setShowFilters(!showFilters)} 
            className={`flex items-center gap-2 px-4 py-2 border rounded-xl text-xs font-bold transition-all ${
              showFilters ? 'bg-red-50 border-red-300 text-primary' : 'bg-white border-gray-300'
            }`}
          >
            <Filter className="h-4 w-4" /> {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-2 border-t border-gray-100 animate-in fade-in slide-in-from-top-2">
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Start Date</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full border rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-red-500" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">End Date</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full border rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-red-500" />
            </div>
            <SelectDropdown label="Division" options={divisions} value={divisionFilter} onChange={setDivisionFilter} valueKey="_id" labelKey="name" searchable />
            <SelectDropdown label="Department" options={departments} value={deptFilter} onChange={setDeptFilter} valueKey="_id" labelKey="name" searchable />
            <button onClick={handleClearFilters} className="text-primary text-xs font-bold flex items-center gap-1 hover:underline self-end pb-3">
              <X className="h-4 w-4" /> Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* KPI Overview Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-4">
        <div className="bg-white border rounded-xl p-4 flex items-center gap-3 shadow-sm">
          <div className="p-3 rounded-xl bg-blue-50 text-blue-600"><Users className="h-5 w-5" /></div>
          <div>
            <p className="text-[10px] text-gray-400 font-bold uppercase">Total Applicants</p>
            <p className="font-extrabold text-gray-800 text-lg mt-0.5">{kpis.totalApplications || 0}</p>
          </div>
        </div>

        <div className="bg-white border rounded-xl p-4 flex items-center gap-3 shadow-sm">
          <div className="p-3 rounded-xl bg-red-50 text-primary"><Briefcase className="h-5 w-5" /></div>
          <div>
            <p className="text-[10px] text-gray-400 font-bold uppercase">Active Jobs</p>
            <p className="font-extrabold text-gray-800 text-lg mt-0.5">{kpis.activeJobs || 0}</p>
          </div>
        </div>

        <div className="bg-white border rounded-xl p-4 flex items-center gap-3 shadow-sm">
          <div className="p-3 rounded-xl bg-green-50 text-green-600"><UserCheck className="h-5 w-5" /></div>
          <div>
            <p className="text-[10px] text-gray-400 font-bold uppercase">Total Hires</p>
            <p className="font-extrabold text-gray-800 text-lg mt-0.5">{kpis.totalHired || 0}</p>
          </div>
        </div>

        <div className="bg-white border rounded-xl p-4 flex items-center gap-3 shadow-sm">
          <div className="p-3 rounded-xl bg-red-50 text-red-500"><UserMinus className="h-5 w-5" /></div>
          <div>
            <p className="text-[10px] text-gray-400 font-bold uppercase">Rejections</p>
            <p className="font-extrabold text-gray-800 text-lg mt-0.5">{kpis.totalRejected || 0}</p>
          </div>
        </div>

        <div className="bg-white border rounded-xl p-4 flex items-center gap-3 shadow-sm">
          <div className="p-3 rounded-xl bg-amber-50 text-amber-500"><Clock className="h-5 w-5" /></div>
          <div>
            <p className="text-[10px] text-gray-400 font-bold uppercase">On Hold</p>
            <p className="font-extrabold text-gray-800 text-lg mt-0.5">{kpis.totalOnHold || 0}</p>
          </div>
        </div>

        <div className="bg-white border rounded-xl p-4 flex items-center gap-3 shadow-sm">
          <div className="p-3 rounded-xl bg-teal-50 text-teal-600"><TrendingUp className="h-5 w-5" /></div>
          <div>
            <p className="text-[10px] text-gray-400 font-bold uppercase">Conversion Rate</p>
            <p className="font-extrabold text-gray-800 text-lg mt-0.5">{kpis.conversionRate || 0}%</p>
          </div>
        </div>
      </div>

      {/* Kanban Board Layout */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <div className="p-5 border-b flex justify-between items-center bg-gray-50/50">
          <div>
            <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wider">Applicant Pipeline Board</h3>
            <p className="text-[10px] text-gray-400 mt-0.5">Horizontal swipe view for active candidate tracking across recruitment stages [4].</p>
          </div>
          <button 
            onClick={() => navigate(`/${portalName}/hr/recruitment-management/jobs/create`)}
            className="bg-primary hover:bg-red-700 text-white font-bold text-xs px-4 py-2 rounded-xl transition-all shadow-sm"
          >
            + Add Job Posting
          </button>
        </div>

        <div className="p-5 overflow-x-auto custom-scrollbar">
          <div className="flex flex-row gap-4 min-w-[1100px] lg:min-w-0 lg:grid lg:grid-cols-4">
            
            {/* COLUMN 1: APPLIED */}
            <div className="bg-slate-50/70 border rounded-xl p-4 space-y-4 flex flex-col h-[320px]">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-slate-500 tracking-wider">APPLIED</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700">{stageCounts.applied}</span>
              </div>
              <div className="flex-1 overflow-y-auto space-y-2.5 custom-scrollbar pr-0.5">
                {stats.jobsPerformance?.filter(j => j.totalApplicants > 0).map((job) => (
                  <div key={job._id} className="bg-white border border-slate-100 rounded-lg p-3 shadow-xs space-y-1.5">
                    <h4 className="font-bold text-slate-800 text-xs truncate">{job.title}</h4>
                    <p className="text-[10px] text-slate-400 flex items-center gap-1"><MapPin className="h-3 w-3" /> {job.jobId} &bull; {job.totalApplicants} applicants</p>
                  </div>
                ))}
              </div>
            </div>

            {/* COLUMN 2: SCREENING */}
            <div className="bg-slate-50/70 border rounded-xl p-4 space-y-4 flex flex-col h-[320px]">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-slate-500 tracking-wider">SCREENING</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700">{stageCounts.screening}</span>
              </div>
              <div className="flex-1 overflow-y-auto space-y-2.5 custom-scrollbar pr-0.5">
                {stats.jobsPerformance?.filter(j => j.totalApplicants > 0).map((job) => (
                  <div key={job._id} className="bg-white border border-slate-100 rounded-lg p-3 shadow-xs">
                    <h4 className="font-bold text-slate-800 text-xs truncate">{job.title}</h4>
                    <p className="text-[10px] text-purple-600 font-bold mt-1.5">Screening In-progress</p>
                  </div>
                ))}
              </div>
            </div>

            {/* COLUMN 3: INTERVIEW */}
            <div className="bg-slate-50/70 border rounded-xl p-4 space-y-4 flex flex-col h-[320px]">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-slate-500 tracking-wider">INTERVIEWS</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">{stageCounts.interview}</span>
              </div>
              <div className="flex-1 overflow-y-auto space-y-2.5 custom-scrollbar pr-0.5">
                {stats.upcomingInterviews?.slice(0, 3).map((schedule, idx) => (
                  <div key={idx} className="bg-white border border-slate-100 rounded-lg p-3 shadow-xs space-y-1">
                    <h4 className="font-bold text-slate-800 text-xs truncate">{schedule.firstName} {schedule.lastName}</h4>
                    <p className="text-[9px] text-slate-400 font-medium truncate">{schedule.jobOpeningId?.title}</p>
                    <span className="inline-block text-[9px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-100 mt-1">
                      Round {schedule.interview?.round} Scheduled
                    </span>
                  </div>
                ))}
                {stats.upcomingInterviews?.length === 0 && (
                  <p className="text-center text-[11px] text-slate-400 italic pt-12">No active interviews scheduled.</p>
                )}
              </div>
            </div>

            {/* COLUMN 4: OFFER */}
            <div className="bg-slate-50/70 border rounded-xl p-4 space-y-4 flex flex-col h-[320px]">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-slate-500 tracking-wider">OFFER & ONBOARD</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700">{stageCounts.offer}</span>
              </div>
              <div className="flex-1 overflow-y-auto space-y-2.5 custom-scrollbar pr-0.5">
                {stats.jobsPerformance?.filter(j => j.numberOfHires > 0).map((job) => (
                  <div key={job._id} className="bg-white border border-slate-100 rounded-lg p-3 shadow-xs space-y-1">
                    <h4 className="font-bold text-slate-800 text-xs truncate">{job.title}</h4>
                    <p className="text-[10px] text-green-600 font-bold flex items-center gap-0.5"><Check className="h-3.5 w-3.5" /> {job.numberOfHires} Onboarding accepted</p>
                  </div>
                ))}
                {stats.jobsPerformance?.filter(j => j.numberOfHires > 0).length === 0 && (
                  <p className="text-center text-[11px] text-slate-400 italic pt-12">No hires logged this cycle.</p>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* SVG Charts Area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Sourcing Donut Chart */}
        <div className="bg-white border rounded-xl p-5 shadow-sm space-y-4">
          <div className="flex justify-between items-center border-b pb-2">
            <h3 className="font-bold text-gray-800 text-xs uppercase tracking-wider">Sourcing Channel metrics</h3>
            <span className="text-[10px] text-gray-400 font-bold">Pie Donut View</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
            <div className="relative flex justify-center items-center">
              <svg width="180" height="180" viewBox="0 0 42 42" className="transform -rotate-90">
                <circle cx="21" cy="21" r="15.91549430918954" fill="transparent" stroke="#f1f5f9" strokeWidth="3" />
                {donutData.map((channel, idx) => {
                  const colors = ["#ef4444", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#6366f1"];
                  const color = colors[idx % colors.length];
                  return (
                    <circle 
                      key={channel._id}
                      cx="21" 
                      cy="21" 
                      r="15.91549430918954" 
                      fill="transparent" 
                      stroke={color} 
                      strokeWidth="3.2" 
                      strokeDasharray={channel.strokeDasharray}
                      strokeDashoffset={channel.strokeDashoffset}
                      className="transition-all duration-500 hover:stroke-[4]"
                    />
                  );
                })}
              </svg>
              <div className="absolute text-center">
                <p className="text-xl font-extrabold text-gray-800">{kpis.totalApplications || 0}</p>
                <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">Applications</p>
              </div>
            </div>

            <div className="space-y-2.5">
              {donutData.map((channel, idx) => {
                const colors = ["bg-red-500", "bg-blue-500", "bg-green-500", "bg-amber-500", "bg-purple-500", "bg-indigo-500"];
                const colorClass = colors[idx % colors.length];
                return (
                  <div key={channel._id} className="flex items-center justify-between text-xs font-semibold">
                    <div className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${colorClass}`} />
                      <span className="capitalize text-gray-600">{channel._id}</span>
                    </div>
                    <span className="text-gray-900">{channel.count} ({channel.percent}%)</span>
                  </div>
                );
              })}
              {donutData.length === 0 && (
                <p className="text-xs text-gray-400 italic">No sourcing data calculated.</p>
              )}
            </div>
          </div>
        </div>

        {/* Funnel Bar Chart (Aligned with stageConfig.js dynamic labels) [3] */}
        <div className="bg-white border rounded-xl p-5 shadow-sm space-y-4">
          <div className="flex justify-between items-center border-b pb-2">
            <h3 className="font-bold text-gray-800 text-xs uppercase tracking-wider">Hiring Funnel Drop-off</h3>
            <span className="text-[10px] text-gray-400 font-bold">Bar Chart View</span>
          </div>

          <div className="flex flex-col justify-end space-y-3 pt-4">
            {stats.pipelineStages?.map((stage) => {
              const maxCount = Math.max(...stats.pipelineStages.map(s => s.count)) || 1;
              const barPercent = ((stage.count / maxCount) * 100).toFixed(0);

              return (
                <div key={stage._id} className="grid grid-cols-4 gap-2 items-center text-xs">
                  {/* 👈 3. Dynamic lookup to map technical stage keys to friendly labels [3] */}
                  <span className="capitalize font-bold text-gray-600 truncate">{getStageLabel(stage._id)}</span>
                  <div className="col-span-3 flex items-center gap-3">
                    <div className="flex-1 bg-gray-100 h-6 rounded-lg overflow-hidden relative group">
                      <div 
                        className="bg-gradient-to-r from-red-400 to-primary h-full rounded-lg transition-all duration-500 group-hover:from-red-500 group-hover:to-red-700" 
                        style={{ width: `${barPercent}%` }} 
                      />
                    </div>
                    <span className="font-extrabold text-gray-700 w-10 shrink-0 text-right">{stage.count}</span>
                  </div>
                </div>
              );
            })}
            {stats.pipelineStages?.length === 0 && (
              <p className="text-center text-xs text-gray-400 italic py-12">No pipeline configurations found.</p>
            )}
          </div>
        </div>

      </div>

      {/* Active Jobs & Recent Interviews Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white border rounded-xl p-5 shadow-sm space-y-4">
          <h3 className="font-bold text-gray-900 text-xs uppercase tracking-wider border-b pb-2">Active Jobs Performance</h3>
          {stats.jobsPerformance?.length > 0 ? (
            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b text-gray-400 uppercase text-[10px] font-bold">
                    <th className="py-2.5">Job Posting Title</th>
                    <th className="py-2.5 text-center">Openings</th>
                    <th className="py-2.5 text-center">Conversions</th>
                    <th className="py-2.5 text-right">Applicants</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.jobsPerformance.map((job) => (
                    <tr key={job._id} className="border-b hover:bg-red-50/20 transition-all font-medium text-gray-700">
                      <td className="py-3 pr-2">
                        <div className="font-bold text-gray-800 text-[13px]">{job.title}</div>
                        <div className="text-[10px] text-gray-400 mt-0.5">{job.jobId}</div>
                      </td>
                      <td className="py-3 text-center">{job.numberOfOpenings}</td>
                      <td className="py-3 text-center">
                        <span className="text-green-600 font-bold bg-green-50 px-2 py-0.5 rounded border border-green-100">
                          {job.numberOfHires} Hired
                        </span>
                      </td>
                      <td className="py-3 text-right font-bold text-primary">{job.totalApplicants} Applied</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-12 text-center text-xs text-gray-400 italic">No job opening performance recorded.</div>
          )}
        </div>

        <div className="lg:col-span-2 bg-white border rounded-xl p-5 shadow-sm space-y-4">
          <h3 className="font-bold text-gray-900 text-xs uppercase tracking-wider border-b pb-2">Upcoming Scheduled Interviews</h3>
          {stats.upcomingInterviews?.length > 0 ? (
            <div className="space-y-3">
              {stats.upcomingInterviews.map((schedule, idx) => (
                <div key={idx} className="border rounded-xl p-3.5 space-y-2.5 bg-gray-50/50 shadow-xs">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="text-[9px] uppercase tracking-wider text-primary font-bold">ROUND {schedule.interview?.round}</span>
                      <h4 className="font-bold text-gray-800 text-xs">{schedule.interview?.roundName || 'Technical Round'}</h4>
                    </div>
                    <span className="px-2 py-0.5 text-[9px] font-bold rounded bg-blue-50 text-blue-700 border border-blue-100">
                      Scheduled
                    </span>
                  </div>

                  <div className="text-[11px] text-gray-500 space-y-1 font-medium">
                    <div className="text-gray-800 font-bold text-xs">{schedule.firstName} {schedule.lastName}</div>
                    <div className="text-[10px] text-gray-400">{schedule.jobOpeningId?.title}</div>
                    <div className="flex items-center gap-1.5 mt-1.5"><Clock className="h-3.5 w-3.5" /> <span>{new Date(schedule.interview?.scheduledAt).toLocaleString()} ({schedule.interview?.durationMinutes} min)</span></div>
                    {schedule.interview?.meetingLink && (
                      <div className="flex items-center gap-1.5 pt-1">
                        <Video className="h-3.5 w-3.5 text-green-600 shrink-0" />
                        <a href={schedule.interview?.meetingLink} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Join Interview Room</a>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center space-y-3 bg-gray-50/30 rounded-xl border border-dashed border-gray-100">
              <Calendar className="h-10 w-10 text-gray-300 animate-pulse" />
              <div>
                <h4 className="text-xs font-bold text-gray-700">No Scheduled Interviews</h4>
                <p className="text-[11px] text-gray-400 mt-0.5">There are no upcoming interview rounds scheduled.</p>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default RecruitmentDashboard;